import { CountryCodeList } from '../../types/countries';
import { SiteLanguage, SiteNameSpace } from 'constants/locale';
import ExprealtyCaTheme from 'themes/exprealtyCA';
import { JoinPageContactPaths } from 'constants/urls';
import { GTM_CLICK_BOOST_CANDIDATE_BUTTON_JOIN_EXP, GTM_CLICK_SIGN_ME_UP_BUTTON_JOIN_EXP, GTM_CLICK_BUILT_FOR_THIS_JOIN_US_BUTTON_JOIN_EXP, GTM_CLICK_LEARN_MORE_BUTTON_JOIN_EXP, GTM_CLICK_THRIVE_CANDIDATE_BUTTON_JOIN_EXP, GTM_CLICK_GET_NOW_BUTTON_JOIN_EXP } from 'constants/events';

import type { ThemeConfig } from 'themes/themeConfig';
import type { Tenant } from '../types';
import { ThemeNames } from 'types/themes';

export const EXP_REALTY_CA_TENANT_ID = 'exprealtyCA' as const;
export const EXP_REALTY_CA_L18N_NAMESPACE = EXP_REALTY_CA_TENANT_ID;
export const EXP_REALTY_CA_L18N_NAMESPACES = [
  EXP_REALTY_CA_L18N_NAMESPACE,
  SiteNameSpace.Common,
  SiteNameSpace.FormError,
  SiteNameSpace.Modal,
] as const;

export const JOIN_EXP_HIGHLIGHT_COLOR = '#1b489b';

const homeConfig = {
  heroVideo: '/ca-hero-1.mp4',
  joinLink: 'https://join.exprealty.ca',
  internalLinks: [
    {
      title: 'Provinces & Territories',
      links: [
        {
          label: 'Alberta',
          link: '/alberta-real-estate',
        },
        {
          label: 'British Columbia',
          link: '/british-columbia-real-estate',
        },
        {
          label: 'Manitoba',
          link: '/manitoba-real-estate',
        },
        {
          label: 'New Brunswick',
          link: '/new-brunswick-real-estate',
        },
        {
          label: 'Newfoundland & Labrador',
          link: '/newfoundland-and-labrador-real-estate',
        },
        {
          label: 'Nova Scotia',
          link: '/nova-scotia-real-estate',
        },
        {
          label: 'Ontario',
          link: '/ontario-real-estate',
        },
        {
          label: 'Quebec',
          link: '/quebec-real-estate',
        },
        {
          label: 'Saskatchewan',
          link: '/saskatchewan-real-estate',
        },
        {
          label: 'Yukon',
          link: '/yukon-real-estate',
        },
      ],
    },
    {
      title: 'Metro Areas',
      links: [
        {
          label: 'Toronto',
          link: '/toronto-on-real-estate',
        },
        {
          label: 'Vancouver',
          link: '/vancouver-bc-real-estate',
        },
        {
          label: 'Calgary',
          link: '/calgary-ab-real-estate',
        },
        {
          label: 'Edmonton',
          link: '/edmonton-ab-real-estate',
        },
        {
          label: 'Ottawa',
          link: '/ottawa-on-real-estate',
        },
        {
          label: 'Hamilton',
          link: '/hamilton-on-real-estate',
        },
        {
          label: 'London',
          link: '/london-on-real-estate',
        },
        {
          label: 'Halifax',
          link: '/halifax-ns-real-estate',
        },
        {
          label: 'St. Catharines',
          link: 'st-catharines-on-real-estate',
        },
      ],
    },
    {
      title: 'Popular Ontario Cities',
      links: [
        {
          label: 'Barrie Homes for Sale',
          link: '/barrie-on-real-estate',
        },
        {
          label: 'Bradford West Homes for Sale',
          link: '/bradford-west-gwillimbury-on-real-estate',
        },
        {
          label: 'Cambridge Homes for Sale',
          link: '/cambridge-on-real-estate',
        },
        {
          label: 'Collingwood Homes for Sale',
          link: '/collingwood-on-real-estate',
        },
        {
          label: 'Guelph Homes for Sale',
          link: '/guelph-on-real-estate',
        },
        {
          label: 'Hamilton Homes for Sale',
          link: '/hamilton-on-real-estate',
        },
        {
          label: 'Kitchener Homes for Sale',
          link: '/kitchener-on-real-estate',
        },
        {
          label: 'London Homes for Sale',
          link: '/london-on-real-estate',
        },
        {
          label: 'Orillia Homes for Sale',
          link: '/orillia-on-real-estate',
        },
        {
          label: 'Ottawa Homes for Sale',
          link: '/ottawa-on-real-estate',
        },
        {
          label: 'Peterborough Homes for Sale',
          link: '/peterborough-on-real-estate',
        },
        {
          label: 'Waterloo Homes for Sale',
          link: '/waterloo-on-real-estate',
        },
      ],
    },
    {
      title: 'Popular Western Canada Cities',
      links: [
        {
          label: 'Vancouver Homes for Sale',
          link: '/vancouver-bc-real-estate',
        },
        {
          label: 'Burnaby Homes for Sale',
          link: '/burnaby-bc-real-estate',
        },
        {
          label: 'Coquitlam Homes for Sale',
          link: '/coquitlam-bc-real-estate',
        },
        {
          label: 'Maple Ridge Homes for Sale',
          link: '/maple-ridge-bc-real-estate',
        },
        {
          label: 'New Westminster Homes for Sale',
          link: '/new-westminster-bc-real-estate',
        },
        {
          label: 'North Vancouver Homes for Sale',
          link: '/north-vancouver-bc-real-estate',
        },
        {
          label: 'Richmond Homes for Sale',
          link: '/richmond-bc-real-estate',
        },
        {
          label: 'Surrey Homes for Sale',
          link: '/surrey-bc-real-estate',
        },
        {
          label: 'Victoria Homes for Sale',
          link: '/victoria-bc-real-estate',
        },
        {
          label: 'White Rock Homes for Sale',
          link: '/white-rock-bc-real-estate',
        },
        {
          label: 'Calgary Homes for Sale',
          link: '/calgary-ab-real-estate',
        },
        {
          label: 'Edmonton Homes for Sale',
          link: '/edmonton-ab-real-estate',
        },
      ],
    },
  ],
  contentCards: {
    sectionTitleParts: ['With eXp, Possibility', 'Becomes', 'Reality'],
    cards: [
      {
        title: 'Buy With Us',
        image: '/next/assets/images/content-cards/buy.jpg',
        titleColor: 'rgba(255, 255, 255, 0.7)',
        backgroundColor: '#163259',
        content: 'Buying a home is one of the most important decisions you will make, and our local experts at eXp Realty are here to make the process as easy as possible.',
        link: '/buy',
        desktopTransform: 'rotate(-5deg)',
        tabletTransform: 'none',
      },
      {
        title: 'Sell With Us',
        image: '/next/assets/images/content-cards/sell.jpg',
        titleColor: 'rgba(255, 255, 255, 0.7)',
        backgroundColor: '#6C82A0',
        content: 'Selling your home can feel like a big project, but our agents are ready to make every step of the journey as smooth as possible. Let an eXp agent be your guide to selling your home like a pro.',
        link: '/sell',
        tabletTransform: 'translateY(3em)',
        translateYTablet: '50',
      },
      {
        title: 'Find an Agent',
        image: '/next/assets/images/content-cards/agent.jpg',
        titleColor: '#163259',
        backgroundColor: '#DAE0E7',
        content: 'Whether you are looking to buy, sell or rent, our experienced and passionate agents are on hand to help make it happen. Find the perfect agent for your needs today!',
        link: '/agents-search',
        desktopTransform: 'rotate(2deg) translateY(-1.5em)',
        tabletTransform: 'none',
        translateYDesktop: '-20',
      },
      {
        title: 'Sold Nearby',
        image: '/next/assets/images/content-cards/sold.jpg',
        titleColor: '#6D6F7C',
        backgroundColor: '#EEEEEE',
        content: 'Find out which properties are being in your favourite neighbourhoods and for how much!',
        link: '/real-estate/sold',
        desktopTransform: 'rotate(7deg) translateY(2em)',
        tabletTransform: 'translateY(3em)',
        translateYDesktop: '40',
        translateYTablet: '50',
      },
    ],
  },
};

const joinExpCANamespace = `join.${EXP_REALTY_CA_L18N_NAMESPACE}`;

export const joinExpCAConfig = {
  namespace: joinExpCANamespace,
  joinFormLink: JoinPageContactPaths[ThemeNames.EXP_REALTY_CA],
  learnMoreClickEvent: GTM_CLICK_LEARN_MORE_BUTTON_JOIN_EXP,
  builtForThisJoinUsClickEvent: GTM_CLICK_BUILT_FOR_THIS_JOIN_US_BUTTON_JOIN_EXP,
  signMeUpClickEvent: GTM_CLICK_SIGN_ME_UP_BUTTON_JOIN_EXP,
  getNowClickEvent: GTM_CLICK_GET_NOW_BUTTON_JOIN_EXP,
  glennQuoteConfig: {
    authorImage: '/next/assets/images/join-expus/Glenn.jpg',
    authorImageAlt: 'Glenn Sanford',
    authorTitle: `${joinExpCANamespace}:glennQuote.authorTitle`,
    authorName: 'Glenn Sanford',
    quoteContent: `${joinExpCANamespace}:glennQuote.quoteContent`,
    ctaText: `${joinExpCANamespace}:glennQuote.ctaText`,
    theme: {
      color: JOIN_EXP_HIGHLIGHT_COLOR,
    },
  },
  leoQuoteConfig: {
    authorImage: '/next/assets/images/join-expus/Leo.jpg',
    authorImageAlt: 'Leo Pareja',
    authorTitle: `${joinExpCANamespace}:leoQuote.authorTitle`,
    authorName: 'Leo Pareja',
    quoteContent: `${joinExpCANamespace}:leoQuote.quoteContent`,
    theme: {
      color: JOIN_EXP_HIGHLIGHT_COLOR,
    },
  },
  videoCardData: [
    {
      videoId: 'hv5oA6q_phM',
      bottomText: `${joinExpCANamespace}:videoCards.card1`,
    },
    {
      videoId: 'AA76L8hw7oU',
      bottomText: `${joinExpCANamespace}:videoCards.card2`,
    },
    {
      videoId: 'pGH14z9XpRc',
      bottomText: `${joinExpCANamespace}:videoCards.card3`,
    },
    {
      videoId: 'vK24zkG4C0k',
      bottomText: `${joinExpCANamespace}:videoCards.card4`,
    },
    {
      videoId: 'VdulH47weDY',
      bottomText: `${joinExpCANamespace}:videoCards.card5`,
    },
    {
      videoId: 'H_DP7Pmme0s',
      bottomText: `${joinExpCANamespace}:videoCards.card6`,
    },
    {
      videoId: 'xcx9cySzqFY',
      bottomText: `${joinExpCANamespace}:videoCards.card7`,
    },
    {
      videoId: 'x0dZCrsGBAw',
      bottomText: `${joinExpCANamespace}:videoCards.card8`,
    },
  ],
  cardStackData: [
    {
      title: `${joinExpCANamespace}:cardStack.freedom.title`,
      description: `${joinExpCANamespace}:cardStack.freedom.description`,
      imageSrc: '/next/assets/images/join-expus/card-stack-image-1.png',
      backgroundColor: '#0C0F24',
      textColor: '#fff',
      imageAlt: `${joinExpCANamespace}:cardStack.freedom.imageAlt`,
    },
    {
      title: `${joinExpCANamespace}:cardStack.ownership.title`,
      description: `${joinExpCANamespace}:cardStack.ownership.description`,
      imageSrc: '/next/assets/images/join-expus/card-stack-image-2.png',
      backgroundColor: '#6D6F7C',
      textColor: '#fff',
      imageAlt: `${joinExpCANamespace}:cardStack.ownership.imageAlt`,
    },
    {
      title: `${joinExpCANamespace}:cardStack.growth.title`,
      description: `${joinExpCANamespace}:cardStack.growth.description`,
      imageSrc: '/next/assets/images/join-expus/card-stack-image-3.png',
      backgroundColor: '#DAE0E7',
      textColor: '#0C0F24',
      imageAlt: `${joinExpCANamespace}:cardStack.growth.imageAlt`,
    },
    {
      title: `${joinExpCANamespace}:cardStack.community.title`,
      description: `${joinExpCANamespace}:cardStack.community.description`,
      imageSrc: '/next/assets/images/join-expus/card-stack-image-4.png',
      backgroundColor: '#EDF2F7',
      textColor: '#0C0F24',
      imageAlt: `${joinExpCANamespace}:cardStack.community.imageAlt`,
    },
  ],
  programsData: [
    {
      image: '/next/assets/images/join-expus/program-3.jpeg',
      imageAlt: `${joinExpCANamespace}:programs.boost.imageAlt`,
      title: `${joinExpCANamespace}:programs.boost.title`,
      description: `${joinExpCANamespace}:programs.boost.description`,
      buttonText: `${joinExpCANamespace}:programs.boost.buttonText`,
      buttonLink: 'https://docs.google.com/forms/d/e/1FAIpQLScb_rXcHYN2yOY7VFAuG1UhvjTwpKRWteFsB_fZhnzsnBZTZg/viewform',
      buttonClickEvent: GTM_CLICK_BOOST_CANDIDATE_BUTTON_JOIN_EXP,
    },
    {
      image: '/next/assets/images/join-expus/program-4.jpeg',
      imageAlt: `${joinExpCANamespace}:programs.thrive.imageAlt`,
      title: `${joinExpCANamespace}:programs.thrive.title`,
      description: `${joinExpCANamespace}:programs.thrive.description`,
      buttonText: `${joinExpCANamespace}:programs.thrive.buttonText`,
      buttonLink: 'https://docs.google.com/forms/d/e/1FAIpQLSeIE4Y1gVhUACFa1DEj_qD0k-1zqWBKn8s6X3a1fCDpvXJodQ/viewform',
      buttonClickEvent: GTM_CLICK_THRIVE_CANDIDATE_BUTTON_JOIN_EXP,
    },
  ],
  faq: {
    title: `${joinExpCANamespace}:faq.title`,
    content: [
      {
        title: `${joinExpCANamespace}:faq.q1.title`,
        children: `${joinExpCANamespace}:faq.q1.children`,
      },
      {
        title: `${joinExpCANamespace}:faq.q2.title`,
        children: `${joinExpCANamespace}:faq.q2.children`,
      },
      {
        title: `${joinExpCANamespace}:faq.q3.title`,
        children: `${joinExpCANamespace}:faq.q3.children`,
        styleOverrides: {
          p: {
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            marginTop: '0.5rem',
          },
        },
      },
      {
        title: `${joinExpCANamespace}:faq.q4.title`,
        children: `${joinExpCANamespace}:faq.q4.children`,
      },
      {
        title: `${joinExpCANamespace}:faq.q5.title`,
        children: `${joinExpCANamespace}:faq.q5.children`,
      },
      {
        title: `${joinExpCANamespace}:faq.q6.title`,
        children: `${joinExpCANamespace}:faq.q6.children`,
      },
      {
        title: `${joinExpCANamespace}:faq.q7.title`,
        children: `${joinExpCANamespace}:faq.q7.children`,
        values: { contactLink: JoinPageContactPaths[ThemeNames.EXP_REALTY_CA] },
      },
    ],
  },
};

export const expRealtyCATenant: Tenant<ThemeConfig> = {
  id: EXP_REALTY_CA_TENANT_ID,
  hosts: ['exprealty.ca', 'zooexptest.ca', 'zooexpprod.ca', 'www.zooexpprod.ca', 'www.exprealty.ca'],
  theme: ExprealtyCaTheme,
  localization: {
    languages: [SiteLanguage.English],
    countries: [CountryCodeList.CANADA],
    namespaces: EXP_REALTY_CA_L18N_NAMESPACES,
    defaultLanguage: SiteLanguage.English,
  },
  config: {
    home: homeConfig,
    joinExp: joinExpCAConfig,
  },
  debug: {
    forceTenantHeaderValue: 'useTenant="useExpRealtyCA"',
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: true,
  },
};